import { useState, useEffect } from "react";
import Card from "components/Card";
import InputField from "components/InputField";
import Button from "components/Button";
import Checkbox from "components/Checkbox";
import { postLeaveType, getLeaveTypes } from "api/apiHelper";
import { toast } from "react-toastify";
import { LeaveType } from "types/leaves-types";
import { Link } from "react-router-dom";
import { usePermissions } from 'app/hooks';


export interface LeaveInfoType {
  id?: number;
  description: string;
  is_paid: boolean;
}

const RegisterLeaveTypeForm = () => {
  const permissions = usePermissions() || [];
  const [leaveData, setLeaveData] = useState<LeaveInfoType>({
    description: "",
    is_paid: false,
  });
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchLeaveTypes = async () => {
    setIsLoading(true);
    try {
      const response = await getLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      console.error("Error fetching leave types:", error);
      toast.error("Failed to fetch leave types");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value, type } = e.target;
    
    if (type === "checkbox") {
      const target = e.target as HTMLInputElement;
      setLeaveData({
        ...leaveData,
        [id]: target.checked
      });
    } else {
      setLeaveData({
        ...leaveData,
        [id]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!leaveData.description.trim()) {
      toast.error("Please enter a leave type description");
      return;
    }

    const notification = toast.loading("Registering Leave Type...");

    try {
      const response = await postLeaveType(leaveData);
      console.log(response);
      toast.update(notification, {
        render: "Leave Type registered successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      handleReset();
      fetchLeaveTypes();
    } catch (error) {
      toast.update(notification, {
        render: "Failed to register Leave Type",
         type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      console.log(error);
    }
  };

  const handleReset = () => {
    setLeaveData({ description: "", is_paid: false });
  };

  return (
    <div className="flex flex-col gap-5">
      <Card extra={"w-full h-full pb-10 p-4"}>
        <div className="mb-8 mt-2 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Register Leave Type
          </h4>
        </div>

        <form className="flex gap-2 w-full flex-col" onSubmit={handleSubmit} onReset={handleReset}>
          <InputField
            label="Leave Type"
            id="description"
            type="text"
            placeholder="Enter Leave Type here..."
            extra="w-full"
            variant="outlined"
            onChange={handleChange}
            value={leaveData.description}
            required
          />
          <div className="flex gap-2 items-center">
            <label
              htmlFor="is_paid"
              className="text-sm text-navy-700 dark:text-white font-bold"
            >
              Is Paid
            </label>
            <Checkbox
              id="is_paid"
              onChange={handleChange}
              checked={leaveData.is_paid}
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button type="reset" varient="secondary" className="w-max">Cancel</Button>
            <Button type="submit" varient="primary" className="w-max">Submit</Button>
          </div>
        </form>
      </Card>

      {/* Leave Types List */}
      <Card extra={"w-full h-full p-4"}>
        <div className="mb-6">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Registered Leave Types
          </h4>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            List of all available leave types in the system
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
          </div>
        ) : leaveTypes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-file-alt text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Leave Types
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              No leave types have been registered yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Leave Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              {permissions.includes("leaves.view_employeeleavetype") && (              
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {leaveTypes.map((leaveType) => (
                    <tr
                      key={leaveType.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                    >
                      <Link
                        to={`/leaves/leavetype/${leaveType.id}/`}
                        className="contents" // ensures link covers entire row without breaking layout
                        aria-label={`View Leave Type ${leaveType.id}`}
                      >
                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                          {leaveType.id}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {leaveType.description}
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              leaveType.is_paid
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"
                            }`}
                          >
                            {leaveType.is_paid ? "Paid" : "Unpaid"}
                          </span>
                        </td>
                      </Link>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RegisterLeaveTypeForm;
